import { ChangeDetectionStrategy, Component, effect, model, untracked, ViewEncapsulation } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { isKnownDataType, KNOWN_DATA_TYPES, simpleDeepClone } from '@phx-erp/shared';
import { Table, TableModule } from 'primeng/table';
import { v4 as uuidv4 } from 'uuid';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { ColumnConfigEx, ColumnDefinitionEx, PrimeColumnDef } from '../../../@core/types/column-config-types-extended';
import { disambiguateDuplicateEntityFieldLabels, IEntityField, translateEntityFieldLabel } from '../../../common/utilities';
import { DeletionResponse, DeletionResult, EntityConfig, EntityFieldConfig } from '../../../generated/generated-types';
import { EntityConfigService } from '../../services/entity-config.service';
import { BaseListWrapperComponent } from '../base-list-wrapper/base-list-wrapper.component';
import { NewBaseListComponent } from '../new-base-list-component/new-base-list-component';
import { PhxInputComponent } from '../phx-input/phx-input.component';
import { PhxSelect } from '../phx-select/phx-select.component';

export interface EntityFieldConfigExtended extends EntityFieldConfig {
  id?: string;
}

@Component({
  selector: 'app-entity-field-config-list-widget',
  imports: [TableModule, BaseListWrapperComponent],
  providers: [EntityConfigService],
  templateUrl: './entity-field-config-list-widget.component.html',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EntityFieldConfigListWidgetComponent extends NewBaseListComponent<EntityConfig | any> {

  entityName = model<string>();
  variant = model<string>();
  dataTypeControl = model<PhxSelect<string>>();
  defaultValueControl = model<PhxInputComponent>();
  activeFields = model<IEntityField[]>([]);

  //alphabetically sorted active field names
  sortedActiveFieldNames = model<IEntityField[]>([]);
  fullItem = model<EntityConfig>(null);
  /** When set, only rows whose `field` starts with this prefix are shown (full `EntityConfig` still holds all rows). */
  fieldNameDisplayPrefix = model<string | null>(null);
  /** When set (and no display prefix), rows whose `field` starts with any of these prefixes are hidden. */
  fieldNameDisplayExcludePrefixes = model<string[] | null>(null);
  /**
   * @description Skip the refresh load this amount of times - because its not necessary
   * @important should be 2 if setValue from outside - since also the newBaseList triggers a refresh with loadFallbackFields
   */
  skipLoadTimes = model<number>(2);

  constructor(
    private entityConfigService: EntityConfigService,
    private i18n: I18nService
  ) {
    super("EntityFieldConfig", entityConfigService);
    this.noCopy = true;
    this.inlineEditMode = true;
    this.addInDetail = false;
    this.baseViewName = "EntityFieldConfig";
    this.offlineMode = true;
    this.offlineColumnSet.set(true);
    this.autoloadItems = false;

    effect(() => {
      let activeFields = this.activeFields();
      untracked(() => {
        if (activeFields?.length > 0) {
          this.sortedActiveFieldNames.set(
            [...activeFields].sort((a, b) => a.label.localeCompare(b.label)),
          );
        } else {
          this.sortedActiveFieldNames.set([]);
        }
      });
    });
  }

  override initTable(table: Table) {
    super.initTable(table);
    void this.loadColumnConfigWithCache();
  }

  private buildDefaultEntityFieldConfigColumns(): ColumnDefinitionEx[] {
    return [
      { field: 'field', dataType: 'String', width: 220 },
      { field: 'dataType', dataType: 'String', width: 120 },
      { field: 'required', dataType: 'boolean', width: 100 },
      { field: 'defaultValue', dataType: 'String', width: 140 },
      { field: 'label', dataType: 'String', width: 140 },
      { field: 'hide', dataType: 'boolean', width: 80 },
      { field: 'className', dataType: 'String', width: 120 },
      { field: 'disabled', dataType: 'boolean', width: 100 },
      { field: 'placeholder', dataType: 'String', width: 140 },
    ];
  }

  override async refresh() {
    if (this.skipLoadTimes() > 0) {
      this.skipLoadTimes.set(this.skipLoadTimes() - 1);
      this.setEntityAndFieldConfig();
      return;
    }
    if (this.fullItem()?.id) {
      this.setEntityAndFieldConfig();
      return;
    }
    this.setLoading(true);
    if (this.entityName())
      this.entityConfigService.getItemDataByEntity(this.entityName(), this.variant()).then((item) => {
        this.setEntityAndFieldConfig(item);
      }).finally(() => {
        this.setLoading(false);
      });
    else
      this.baseServices.notificationService.error(_('entity-field-config.entity-name-not-set'));
    return;
  }

  setEntityAndFieldConfig(item?: EntityConfig) {
    if (!this.fullItem() && !item)
      this.fullItem.set({
        entityName: this.entityName(),
        variant: this.variant(),
        entityFieldConfigs: []
      } as EntityConfig);
    else {
      let copiedItem = simpleDeepClone(item ?? this.fullItem());
      this.fullItem.set(copiedItem);
      let entityFieldConfigs = copiedItem.entityFieldConfigs as EntityFieldConfigExtended[];
      entityFieldConfigs.forEach(item => {
        item.id = item.id || uuidv4();
      });
    }
    this.mergeConfigFieldsIntoActiveFields();
    const allConfigs = (this.fullItem().entityFieldConfigs ?? []) as EntityFieldConfigExtended[];
    this.setItems(this.filterEntityFieldConfigsForDisplay(allConfigs));
  }

  /** Configured fields may be hidden on the parent form and missing from parentActiveFields. */
  private mergeConfigFieldsIntoActiveFields(): void {
    const entityName = this.entityName();
    const configs = this.fullItem()?.entityFieldConfigs ?? [];
    if (!configs.length) {
      return;
    }
    const merged = [...this.activeFields()];
    for (const config of configs) {
      if (!config.field || merged.some((f) => f.fieldName === config.field)) {
        continue;
      }
      merged.push({
        fieldName: config.field,
        type: config.dataType ?? null,
        label: entityName
          ? translateEntityFieldLabel(entityName, config.field, this.i18n)
          : config.field,
      });
    }
    if (merged.length !== this.activeFields().length) {
      this.activeFields.set(
        entityName
          ? disambiguateDuplicateEntityFieldLabels(merged, entityName, this.i18n)
          : merged,
      );
    }
  }

  private resolveEntityFieldLabel(fieldName?: string | null): string {
    if (!fieldName) {
      return '';
    }
    const fromActive = this.activeFields()?.find((item) => item.fieldName === fieldName);
    if (fromActive?.label) {
      return fromActive.label;
    }
    const entityName = this.entityName();
    if (entityName) {
      return translateEntityFieldLabel(entityName, fieldName, this.i18n);
    }
    return fieldName;
  }

  private filterEntityFieldConfigsForDisplay(configs: EntityFieldConfigExtended[]): EntityFieldConfigExtended[] {
    const prefix = this.fieldNameDisplayPrefix();
    if (prefix) {
      return configs.filter((c) => c.field?.startsWith(prefix));
    }
    const exclude = this.fieldNameDisplayExcludePrefixes();
    if (exclude?.length) {
      return configs.filter((c) => !c.field || !exclude.some((p) => c.field.startsWith(p)));
    }
    return configs;
  }

  private async resolveEntityConfigForSave(): Promise<EntityConfig> {
    const current = this.fullItem();
    if (current?.id || !this.entityName()) {
      return current;
    }
    const existing = await this.entityConfigService.getItemDataByEntity(
      this.entityName(),
      this.variant(),
    );
    if (!existing?.id) {
      return current;
    }
    const merged: EntityConfig = {
      ...existing,
      ...current,
      id: existing.id,
      version: existing.version,
      entityName: current?.entityName ?? existing.entityName,
      variant: current?.variant ?? existing.variant,
      entityFieldConfigs: current?.entityFieldConfigs ?? existing.entityFieldConfigs,
    };
    this.fullItem.set(merged);
    return merged;
  }

  override async onRowEditSave(rowData?: EntityFieldConfigExtended): Promise<EntityFieldConfigExtended | null> {
    let newRowData = { ...rowData };
    if (!newRowData.dataType) {
      let fieldType = this.activeFields()?.find(item => item.fieldName === rowData.field)?.type;
      if (isKnownDataType(fieldType)) {
        newRowData.dataType = fieldType;
      }
    }
    delete (newRowData as EntityFieldConfigExtended).id;
    // check in fullItems if the item is already there
    let existingItem = null;
    if (this.fullItem()?.entityFieldConfigs)
      existingItem = this.fullItem()?.entityFieldConfigs.find(item => item.field === rowData.field);
    else
      this.fullItem().entityFieldConfigs = [];
    if (existingItem) {
      existingItem = { ...existingItem, ...newRowData };
      this.fullItem.update(item => {
        item.entityFieldConfigs = item.entityFieldConfigs.map(item => item.field === rowData.field ? existingItem : item);
        return item;
      });
    }
    else {
      this.fullItem.update(item => {
        item.entityFieldConfigs = [...item.entityFieldConfigs, newRowData];
        return item;
      });
    }
    let saved: EntityConfig;
    try {
      saved = await this.saveInCRUDService(await this.resolveEntityConfigForSave());
    }
    catch (error) {
      this.baseServices.notificationService.error(error);
      return;
    }
    let savedItem: EntityFieldConfigExtended = saved.entityFieldConfigs.find(item => item.field === rowData.field);
    savedItem.id = rowData.id;

    this.itemsSig.update(items => {
      const index = items.findIndex(item => item.field === rowData.field);
      if (index !== -1) {
        items[index] = savedItem;
      }
      return items;
    });

    this.endEdit(rowData, savedItem);

    return savedItem;
  }

  override async deleteItems(itemsToDelete?: EntityFieldConfig[], showNotification?: boolean): Promise<DeletionResponse[]> {
    const base = await this.resolveEntityConfigForSave();
    let entityConfig: EntityConfig = {
      ...base,
      entityFieldConfigs: base.entityFieldConfigs.filter(item => !itemsToDelete?.find(itemToDelete => itemToDelete.field === item.field)),
    };
    this.fullItem.set(entityConfig);
    this.setEntityAndFieldConfig();

    let returnValue: DeletionResponse[];
    try {
      await this.saveInCRUDService(entityConfig);
      returnValue = [{
        result: DeletionResult.DELETED,
        message: 'EntityFieldConfig deleted'
      } as DeletionResponse];
    } catch (error) {
      returnValue = [{
        result: DeletionResult.NOT_DELETED,
        message: 'EntityFieldConfig not deleted'
      } as DeletionResponse];
    }
    return returnValue;
  }

  protected override setColumnConfig(colConfig: ColumnConfigEx) {
    if (!colConfig.columns?.length) {
      colConfig.columns = this.buildDefaultEntityFieldConfigColumns();
    }

    for (let cd of colConfig.columns) {
      let fieldLowerCase = cd.field?.toLowerCase();
      if (fieldLowerCase === 'required' || fieldLowerCase === 'defaultValue' || fieldLowerCase === 'label' || fieldLowerCase === 'hide' || fieldLowerCase === 'className' || fieldLowerCase === 'disabled' || fieldLowerCase === 'placeholder'
      )
        cd.canEdit = true;

      if (fieldLowerCase === 'defaultvalue') {
        cd.getEditComponent = () => PhxInputComponent;
        cd.editComponentCreated = (c: PhxInputComponent, rowData: EntityFieldConfig, colDef: ColumnDefinitionEx) => {
          this.defaultValueControl.set(c);
          if (rowData.defaultValue)
            c.setValue(rowData.defaultValue);
        }
      }

      if (fieldLowerCase === 'field') {
        cd.canEdit = true;
        cd.getEditComponent = () => PhxSelect;
        cd.editComponentCreated = (c: PhxSelect<IEntityField>, rowData: EntityFieldConfig, colDef: ColumnDefinitionEx) => {
          c.items = this.sortedActiveFieldNames;
          c.optionLabel.set('label');
          if (rowData.field && !(c.items() as IEntityField[]).find(item => item.fieldName === rowData.field)) {
            c.items.set([...(c.items() as IEntityField[]), this.activeFields()?.find(item => item.fieldName === rowData.field)]);
          }
          if (rowData.field)
            c.value.set(this.activeFields()?.find(item => item.fieldName === rowData.field));
          c.onModelChange.subscribe((value: IEntityField) => {
            rowData.field = value.fieldName;
            this.setColumnsSettingsBasedOnField(value.fieldName, rowData);
          });
        }
      }

      if (fieldLowerCase === 'datatype') {
        cd.canEdit = false;
        cd.getEditComponent = () => PhxSelect;
        cd.editComponentCreated = (c: PhxSelect<string>, rowData: EntityFieldConfig, colDef: ColumnDefinitionEx) => {
          c.items.set(KNOWN_DATA_TYPES);
          this.dataTypeControl.set(c);
          if (rowData.dataType)
            c.value.set(rowData.dataType);
          c.disabled.set(true);
          c.onModelChange.subscribe((value: string) => {
            rowData.dataType = value;
          });
        }
      }
    }
    super.setColumnConfig(colConfig);
  }

  override getDisplayComponent(outputCell: HTMLElement, outputSpan: HTMLElement, col: PrimeColumnDef, rowData: EntityFieldConfig, row: HTMLTableRowElement) {
    if (col.field === 'field') {
      outputSpan.innerHTML = this.resolveEntityFieldLabel(rowData.field);
      return;
    }
    return super.getDisplayComponent(outputCell, outputSpan, col, rowData, row);
  }

  setColumnsSettingsBasedOnField(field: string, rowData: EntityFieldConfig) {
    let fieldType = this.activeFields()?.find(item => item.fieldName === field)?.type;
    if (!isKnownDataType(fieldType)) {
      this.defaultValueControl().disabled.set(true);
      this.defaultValueControl().ngModel = null;
      this.defaultValueControl().tooltip.set(this.i18n.translate(_('error.feature-not-supported-yet')));
    }
    else {
      this.defaultValueControl().disabled.set(false);
      this.defaultValueControl().tooltip.set(null);
    }
    if (fieldType) {
      this.dataTypeControl().value.set(fieldType);
      rowData.dataType = fieldType;
    }
  }

}
